import React, { useEffect, useMemo, useRef, useState } from "react";

const normalizeWord = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.,!?;:()[\]{}"“”'`]+$/g, "");
};

const getSegmentPreview = (targetSegment, maxWords = 6) => {
  const words = Array.isArray(targetSegment?.words)
    ? targetSegment.words
        .map((word) => String(word?.text ?? "").trim())
        .filter(Boolean)
    : [];

  if (!words.length) return "";

  const visibleWords = words.slice(0, maxWords);
  const preview = visibleWords.join(" ");

  return words.length > maxWords
    ? `${preview}…`
    : preview;
};

const getWordScoreColor = (score, errorType) => {
  if (errorType === "Omission") return "bg-gray-400";
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getPhonemeDotColor = (score) => {
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getWordAssessmentClasses = (score, errorType) => {
  if (errorType === "Omission") {
    return "bg-gray-50 border-gray-300 text-gray-800";
  }

  if (typeof score !== "number") {
    return "bg-white/90 border-slate-200/90 text-gray-800";
  }

  if (score >= 80) {
    return "bg-green-50 border-green-200 text-gray-800";
  }

  if (score >= 40) {
    return "bg-yellow-50 border-yellow-200 text-gray-800";
  }

  return "bg-red-50 border-red-200 text-gray-800";
};

const getActiveRingClass = (practiceMode) => {
  if (practiceMode === "shadowing") {
    return "ring-2 ring-orange-500 border-orange-300 shadow-lg shadow-orange-500/20 scale-105";
  }

  if (practiceMode === "echoing") {
    return "ring-2 ring-blue-500 border-blue-300 shadow-lg shadow-blue-500/20 scale-105";
  }

  return "ring-2 ring-emerald-500 border-emerald-300 shadow-lg shadow-emerald-500/20 scale-105";
};

const buildSegmentAssessmentMap = (segmentWords = [], assessmentWords = []) => {
  let pointer = 0;

  return segmentWords.map((item) => {
    const segmentText = normalizeWord(item?.text);

    if (!segmentText) return null;

    for (let i = pointer; i < assessmentWords.length; i++) {
      if (normalizeWord(assessmentWords[i]?.Word) === segmentText) {
        pointer = i + 1;
        return assessmentWords[i];
      }
    }

    const fallback =
      assessmentWords.find(
        (word) => normalizeWord(word?.Word) === segmentText
      ) || null;

    return fallback;
  });
};

const SentencePracticeBlock = ({
  segment,
  segments,
  assessment,
  renderHelpPill,
  renderCommentsPill,
  isMetadataNoHelp,
  setShowExerciseHelp,
  handlePreviousSegment,
  handleNextSegment,
  handleSelectSegment,
  showCenterCue,
  practiceMode,
  echoingPhase,
  isCueActive,
  recordingStatus,
  cuePulseCount,
  echoingPulseCount,
  currentWord,
  handleWordPlay,
  openWordMenu,
  handleRangePlay,
  showWords = true,
  enableWordActions = true,
  isPlayingSegment = false,
  showWordLane = true,
  
}) => {
  const selectionAreaRef = useRef(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);
  const [previewSentence, setPreviewSentence] = useState(null);
  const [transitionPhase, setTransitionPhase] = useState("idle");
  const [transitionDirection, setTransitionDirection] = useState(1);
  const [markerPulse, setMarkerPulse] = useState(false);

  const navigationTimerRef = useRef(null);
  const transitionTimerRef = useRef(null);
  const markerTimerRef = useRef(null);
  const previousSegmentIdRef = useRef(Number(segment?.id ?? 0));
  const pendingDirectionRef = useRef(1);

  const normalizedSelection = useMemo(() => {
    if (selectionStart == null || selectionEnd == null) return null;

    return {
      start: Math.min(selectionStart, selectionEnd),
      end: Math.max(selectionStart, selectionEnd),
    };
  }, [selectionStart, selectionEnd]);

  const clearSelection = () => {
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setShowSelectionMenu(false);
  };

  const playSelectedRange = () => {
    if (!normalizedSelection || !segment?.words?.length) return;

    const { start, end } = normalizedSelection;

    handleRangePlay?.({
      startIndex: start,
      endIndex: end,
    });
  };

  const handleWordMouseDown = (index, e) => {
    if (!enableWordActions || practiceMode === "shadowing") return;

    e.preventDefault();
    setIsSelecting(true);
    setSelectionStart(index);
    setSelectionEnd(index);
    setShowSelectionMenu(false);
  };

  const handleWordMouseEnter = (index) => {
    if (!isSelecting) return;
    setSelectionEnd(index);
  };

  const handleWordMouseUp = () => {
    if (!isSelecting) return;

    setIsSelecting(false);

    if (
      selectionStart != null &&
      selectionEnd != null &&
      selectionStart !== selectionEnd
    ) {
      setShowSelectionMenu(true);
    }
  };

  useEffect(() => {
    return () => {
      if (navigationTimerRef.current != null) {
        window.clearTimeout(navigationTimerRef.current);
      }

      if (transitionTimerRef.current != null) {
        window.clearTimeout(transitionTimerRef.current);
      }

      if (markerTimerRef.current != null) {
        window.clearTimeout(markerTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    clearSelection();
  }, [segment?.id]);

  useEffect(() => {
    if (!enableWordActions || !showWords) clearSelection();
  }, [enableWordActions, showWords]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSelecting) {
        setIsSelecting(false);

        if (
          selectionStart != null &&
          selectionEnd != null &&
          selectionStart !== selectionEnd
        ) {
          setShowSelectionMenu(true);
        }
      }
    };

    const handleDocumentMouseDown = (e) => {
      if (!selectionAreaRef.current) return;

      if (!selectionAreaRef.current.contains(e.target)) {
        clearSelection();
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isSelecting, selectionStart, selectionEnd]);

  const mappedAssessmentWords = useMemo(() => {
    const assessmentWords = assessment?.pronunciation?.NBest?.[0]?.Words || [];
    return buildSegmentAssessmentMap(segment?.words || [], assessmentWords);
  }, [segment?.words, assessment]);

  useEffect(() => {
    const nextSegmentId = Number(segment?.id ?? 0);
    const previousSegmentId = previousSegmentIdRef.current;

    if (nextSegmentId === previousSegmentId) {
      return;
    }

    const inferredDirection =
      nextSegmentId > previousSegmentId ? 1 : -1;
    const direction =
      pendingDirectionRef.current || inferredDirection;

    previousSegmentIdRef.current = nextSegmentId;
    setTransitionDirection(direction);
    setTransitionPhase("enter-prep");

    const frame = window.requestAnimationFrame(() => {
      setTransitionPhase("enter");
    });

    if (transitionTimerRef.current != null) {
      window.clearTimeout(transitionTimerRef.current);
    }

    transitionTimerRef.current = window.setTimeout(() => {
      setTransitionPhase("idle");
      transitionTimerRef.current = null;
    }, 185);

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [segment?.id]);

  const isFirstSegment = Number(segment?.id ?? 0) <= 0;
  const isLastSegment =
    Number(segment?.id ?? 0) >= Math.max(0, segments.length - 1);

  const currentSentence = Number(segment?.id ?? 0) + 1;
  const totalSentences = Math.max(1, segments.length);
  const useProgressSegments = totalSentences <= 12;
  const canSelectSentence =
    typeof handleSelectSegment === "function";

  const sentencePosition =
    totalSentences <= 1
      ? 0
      : ((currentSentence - 1) / (totalSentences - 1)) * 100;

  const previewPosition =
    previewSentence == null || totalSentences <= 1
      ? null
      : ((previewSentence - 1) / (totalSentences - 1)) * 100;

  // Short exercises show one node per sentence. Long exercises use evenly
  // spaced range ticks over the same continuous timeline while pointer
  // position still resolves to the exact sentence.
  const timelineTickCount = Math.min(20, totalSentences);

  const previousSentencePreview = useMemo(
    () => getSegmentPreview(segments?.[currentSentence - 2]),
    [segments, currentSentence]
  );

  const nextSentencePreview = useMemo(
    () => getSegmentPreview(segments?.[currentSentence]),
    [segments, currentSentence]
  );

  const hoveredSentencePreview = useMemo(
    () =>
      previewSentence == null
        ? ""
        : getSegmentPreview(segments?.[previewSentence - 1]),
    [segments, previewSentence]
  );

  const sentenceCounter =
    `Sentence ${currentSentence} of ${totalSentences}`;

  const clampSentenceNumber = (value) => {
    return Math.min(
      totalSentences,
      Math.max(1, Math.round(Number(value) || 1))
    );
  };

  const confirmMarkerSelection = () => {
    setMarkerPulse(true);

    if (markerTimerRef.current != null) {
      window.clearTimeout(markerTimerRef.current);
    }

    markerTimerRef.current = window.setTimeout(() => {
      setMarkerPulse(false);
      markerTimerRef.current = null;
    }, 320);
  };

  const navigateToSentence = (
    sentenceNumber,
    { confirmMarker = false } = {}
  ) => {
    const nextSentence = clampSentenceNumber(sentenceNumber);

    if (nextSentence === currentSentence) {
      return;
    }

    const direction =
      nextSentence > currentSentence ? 1 : -1;

    clearSelection();
    setPreviewSentence(null);
    setTransitionDirection(direction);
    setTransitionPhase("exit");
    pendingDirectionRef.current = direction;

    if (confirmMarker) {
      confirmMarkerSelection();
    }

    if (navigationTimerRef.current != null) {
      window.clearTimeout(navigationTimerRef.current);
    }

    navigationTimerRef.current = window.setTimeout(() => {
      if (canSelectSentence) {
        handleSelectSegment(nextSentence - 1);
      } else if (
        direction < 0 &&
        nextSentence === currentSentence - 1
      ) {
        handlePreviousSegment?.();
      } else if (
        direction > 0 &&
        nextSentence === currentSentence + 1
      ) {
        handleNextSegment?.();
      }

      navigationTimerRef.current = null;
    }, 72);
  };

  const sentenceFromPointer = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();

    if (!rect.width) {
      return currentSentence;
    }

    const ratio = Math.min(
      1,
      Math.max(0, (event.clientX - rect.left) / rect.width)
    );

    return clampSentenceNumber(
      1 + ratio * Math.max(0, totalSentences - 1)
    );
  };

  const handleContinuousProgressKeyDown = (event) => {
    if (!canSelectSentence) return;

    let nextSentence = null;

    if (event.key === "ArrowLeft") {
      nextSentence = currentSentence - 1;
    } else if (event.key === "ArrowRight") {
      nextSentence = currentSentence + 1;
    } else if (event.key === "Home") {
      nextSentence = 1;
    } else if (event.key === "End") {
      nextSentence = totalSentences;
    } else if (event.key === "PageUp") {
      nextSentence = currentSentence - 10;
    } else if (event.key === "PageDown") {
      nextSentence = currentSentence + 10;
    }

    if (nextSentence == null) return;

    event.preventDefault();
    event.stopPropagation();
    navigateToSentence(nextSentence, { confirmMarker: true });
  };

  const handleStageKeyDown = (event) => {
    const targetTag = event.target?.tagName?.toLowerCase();

    if (
      targetTag === "button" ||
      targetTag === "input" ||
      targetTag === "textarea" ||
      targetTag === "select"
    ) {
      return;
    }

    if (event.key === "ArrowLeft" && !isFirstSegment) {
      event.preventDefault();
      navigateToSentence(currentSentence - 1);
    }

    if (event.key === "ArrowRight" && !isLastSegment) {
      event.preventDefault();
      navigateToSentence(currentSentence + 1);
    }
  };

  const renderSentenceStatus = () => {
    if (!showCenterCue) {
      const displayedSentence =
        previewSentence ?? currentSentence;
      const isPreviewing =
        previewSentence != null &&
        previewSentence !== currentSentence;

      return (
        <nav
          className="group relative flex h-9 w-[min(78vw,440px)] select-none items-center gap-3"
          aria-label="Sentence navigation"
          onMouseLeave={() => setPreviewSentence(null)}
        >
          <span
            className={[
              "w-[68px] shrink-0 text-right text-[9px] font-bold uppercase tracking-[0.18em] transition-colors duration-150",
              isPreviewing
                ? "text-blue-600"
                : "text-slate-400",
            ].join(" ")}
          >
            {isPreviewing ? "Go to" : "Sentence"}
          </span>

          {useProgressSegments ? (
            <div
              className="relative flex h-9 min-w-0 flex-1 items-center"
              aria-label={sentenceCounter}
            >
              <div
                className="pointer-events-none absolute left-3 right-3 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-slate-300/95"
                aria-hidden="true"
              />

              {totalSentences > 1 && (
                <div
                  className="pointer-events-none absolute left-3 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-[width] duration-250 ease-out"
                  style={{
                    width: `calc((100% - 24px) * ${
                      (currentSentence - 1) /
                      Math.max(1, totalSentences - 1)
                    })`,
                  }}
                  aria-hidden="true"
                />
              )}

              <div className="relative flex w-full items-center justify-between">
                {Array.from(
                  { length: totalSentences },
                  (_, index) => {
                    const number = index + 1;
                    const isCurrent =
                      number === currentSentence;
                    const isPreview =
                      number === previewSentence;
                    const isComplete =
                      number < currentSentence;

                    return (
                      <button
                        key={number}
                        type="button"
                        onClick={() =>
                          navigateToSentence(number, { confirmMarker: true })
                        }
                        onMouseEnter={() =>
                          setPreviewSentence(number)
                        }
                        onFocus={() =>
                          setPreviewSentence(number)
                        }
                        onBlur={() =>
                          setPreviewSentence(null)
                        }
                        disabled={!canSelectSentence}
                        aria-label={`Go to sentence ${number}`}
                        aria-current={
                          isCurrent ? "step" : undefined
                        }
                        title={`Sentence ${number}`}
                        className={[
                          "group/node relative flex h-9 w-8 shrink-0 items-center justify-center rounded-full outline-none",
                          canSelectSentence
                            ? "cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-400/65 focus-visible:ring-offset-2"
                            : "cursor-default",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "relative block rounded-full transition-all duration-180 ease-out",
                            isCurrent
                              ? "h-3 w-3 border-2 border-white bg-blue-600 shadow-[0_2px_8px_rgba(37,99,235,0.32)] ring-2 ring-blue-100/80"
                              : isPreview
                              ? "h-3 w-3 bg-blue-500 shadow-[0_2px_8px_rgba(59,130,246,0.30)] ring-2 ring-white"
                              : isComplete
                              ? "h-2 w-2 bg-blue-400 ring-2 ring-white"
                              : "h-2.5 w-2.5 border border-white bg-slate-300 shadow-[0_1px_3px_rgba(15,23,42,0.16)] ring-1 ring-slate-400/45 group-hover/node:h-3 group-hover/node:w-3 group-hover/node:bg-blue-300 group-hover/node:ring-blue-400/55",
                          ].join(" ")}
                          aria-hidden="true"
                        />

                        {isCurrent && markerPulse && (
                          <span
                            className="pointer-events-none absolute h-5 w-5 animate-ping rounded-full bg-blue-400/30"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          ) : (
            <div
              role="slider"
              tabIndex={canSelectSentence ? 0 : -1}
              aria-label="Select sentence"
              aria-valuemin={1}
              aria-valuemax={totalSentences}
              aria-valuenow={currentSentence}
              aria-valuetext={sentenceCounter}
              aria-disabled={!canSelectSentence}
              onKeyDown={handleContinuousProgressKeyDown}
              onPointerMove={(event) => {
                if (!canSelectSentence) return;

                setPreviewSentence(
                  sentenceFromPointer(event)
                );
              }}
              onPointerLeave={() =>
                setPreviewSentence(null)
              }
              onClick={(event) => {
                if (!canSelectSentence) return;

                navigateToSentence(
                  sentenceFromPointer(event),
                  { confirmMarker: true }
                );
              }}
              className={[
                "relative flex h-9 min-w-0 flex-1 items-center outline-none",
                canSelectSentence
                  ? "cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-400/65 focus-visible:ring-offset-2"
                  : "cursor-default",
              ].join(" ")}
            >
              <div className="relative h-5 w-full">
                <div
                  className="absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-slate-300"
                  aria-hidden="true"
                />

                <div
                  className="absolute left-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-[width] duration-200 ease-out"
                  style={{
                    width: `${sentencePosition}%`,
                  }}
                  aria-hidden="true"
                />

                <div
                  className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-between"
                  aria-hidden="true"
                >
                  {Array.from(
                    { length: timelineTickCount },
                    (_, tickIndex) => {
                      const tickRatio =
                        timelineTickCount <= 1
                          ? 0
                          : tickIndex /
                            (timelineTickCount - 1);
                      const tickSentence =
                        1 +
                        tickRatio *
                          Math.max(
                            0,
                            totalSentences - 1
                          );
                      const isPassed =
                        tickSentence <= currentSentence;

                      return (
                        <span
                          key={tickIndex}
                          className={[
                            "block h-2 w-2 shrink-0 rounded-full border border-white shadow-[0_1px_3px_rgba(15,23,42,0.16)] ring-1 transition-colors duration-150",
                            isPassed
                              ? "bg-blue-400 ring-blue-500/35"
                              : "bg-slate-400 ring-slate-500/35",
                          ].join(" ")}
                        />
                      );
                    }
                  )}
                </div>

                {previewPosition != null &&
                  previewSentence !== currentSentence && (
                    <span
                      className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-400 shadow-[0_2px_8px_rgba(59,130,246,0.28)]"
                      style={{
                        left: `${previewPosition}%`,
                      }}
                      aria-hidden="true"
                    />
                  )}

                <span
                  className="pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-600 shadow-[0_2px_9px_rgba(37,99,235,0.34)] ring-2 ring-blue-100/80 transition-[left] duration-200 ease-out"
                  style={{
                    left: `${sentencePosition}%`,
                  }}
                  aria-hidden="true"
                />

                {markerPulse && (
                  <span
                    className="pointer-events-none absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full bg-blue-400/25"
                    style={{
                      left: `${sentencePosition}%`,
                    }}
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>
          )}

          <span className="flex w-[58px] shrink-0 items-baseline justify-start gap-1 whitespace-nowrap tabular-nums">
            <span
              key={displayedSentence}
              className={[
                "min-w-[1.35em] text-right text-sm font-bold leading-none transition-colors duration-150",
                isPreviewing
                  ? "text-blue-700"
                  : "text-slate-800",
              ].join(" ")}
            >
              {displayedSentence}
            </span>

            <span className="text-[9px] font-semibold text-slate-400">
              of
            </span>

            <span className="text-[11px] font-semibold leading-none text-slate-600">
              {totalSentences}
            </span>
          </span>

          {isPreviewing && hoveredSentencePreview && (
            <div
              className="pointer-events-none absolute left-1/2 top-[34px] z-50 w-max max-w-[280px] -translate-x-1/2 rounded-xl border border-slate-200/90 bg-white/95 px-3 py-2 text-left shadow-[0_12px_32px_-18px_rgba(15,23,42,0.45)] backdrop-blur-xl"
              role="tooltip"
            >
              <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-blue-500">
                Sentence {previewSentence}
              </div>
              <div className="mt-0.5 truncate text-xs font-medium text-slate-700">
                {hoveredSentencePreview}
              </div>
            </div>
          )}
        </nav>
      );
    }

    return (
      <div className="inline-flex min-h-[38px] items-center gap-2 rounded-xl border border-slate-200/90 bg-white/95 px-3 py-2 text-xs font-semibold text-slate-700 shadow-[0_10px_28px_-20px_rgba(15,23,42,0.55)] ring-1 ring-white/90 backdrop-blur-xl">
        <span
          className={[
            "transition-all duration-200",
            practiceMode === "shadowing" &&
            recordingStatus === "recording"
              ? "scale-105 text-emerald-600"
              : practiceMode === "echoing"
              ? "text-blue-700"
              : "text-amber-700",
          ].join(" ")}
        >
          {practiceMode === "shadowing" &&
          recordingStatus === "recording"
            ? "Go!"
            : "Get ready…"}
        </span>

        {practiceMode === "shadowing" &&
          isCueActive &&
          recordingStatus !== "recording" && (
            <div className="flex items-center gap-1" aria-hidden="true">
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  cuePulseCount >= 1
                    ? "bg-amber-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
              <span className="h-0.5 w-3 bg-slate-300" />
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  cuePulseCount >= 2
                    ? "bg-orange-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
              <span className="h-0.5 w-3 bg-slate-300" />
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  cuePulseCount >= 3
                    ? "animate-pulse bg-emerald-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
            </div>
          )}

        {practiceMode === "echoing" &&
          echoingPhase === "paused" && (
            <div className="flex items-center gap-1" aria-hidden="true">
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  echoingPulseCount >= 1
                    ? "bg-blue-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
              <span className="h-0.5 w-3 bg-slate-300" />
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  echoingPulseCount >= 2
                    ? "bg-indigo-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
              <span className="h-0.5 w-3 bg-slate-300" />
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  echoingPulseCount >= 3
                    ? "animate-pulse bg-emerald-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
            </div>
          )}
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/50 bg-white/80 shadow-lg shadow-blue-500/5 backdrop-blur-sm">
      <div className="relative flex min-h-[52px] items-center justify-center px-3 py-2 sm:px-4">
        <div className="flex min-w-0 items-center justify-center gap-2">
          {showWords ? renderHelpPill() : null}
          {showWords ? renderCommentsPill?.() : null}
        </div>

        {showWords && isMetadataNoHelp && (
          <button
            type="button"
            onClick={() => setShowExerciseHelp(true)}
            className="absolute right-3 inline-flex h-9 items-center gap-1.5 rounded-xl border border-indigo-200/70 bg-white/75 px-3 text-xs font-bold text-indigo-700 shadow-sm transition-all duration-200 hover:-translate-y-px hover:bg-white hover:shadow-md sm:right-4 sm:h-10 sm:rounded-full sm:px-4 sm:text-sm"
            title="About this course"
          >
            <span aria-hidden="true">✨</span>
            <span className="hidden sm:inline">About this course</span>
            <span className="sm:hidden">About</span>
            <span className="opacity-60" aria-hidden="true">›</span>
          </button>
        )}
      </div>

      <div
        ref={selectionAreaRef}
        tabIndex={0}
        onKeyDown={handleStageKeyDown}
        className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-r from-blue-50/55 via-white/90 to-indigo-50/55 shadow-[0_18px_50px_-38px_rgba(30,64,175,0.55)] ring-1 ring-white/80 outline-none transition-shadow duration-200 focus-visible:ring-2 focus-visible:ring-blue-400/50 focus-visible:ring-offset-2"
        aria-label={`${sentenceCounter}. Use the left and right arrow keys to change sentence.`}
      >
        {isFirstSegment ? (
          <div
            className="absolute inset-y-0 left-0 w-12 sm:w-32"
            aria-hidden="true"
          />
        ) : (
          <button
            type="button"
            onClick={() =>
              navigateToSentence(currentSentence - 1)
            }
            aria-label="Previous sentence"
            className="group absolute inset-y-0 left-0 z-30 flex w-12 items-center justify-start px-3 text-slate-500 outline-none transition-colors duration-200 hover:text-blue-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400/55 sm:w-32 sm:justify-center sm:px-4"
          >
            <span
              className="pointer-events-none absolute inset-y-0 left-0 right-0 bg-gradient-to-r from-blue-100/70 via-blue-50/35 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
              aria-hidden="true"
            />

            <span
              className="relative text-xl leading-none transition-transform duration-200 group-hover:-translate-x-0.5 group-focus-visible:-translate-x-0.5"
              aria-hidden="true"
            >
              ‹
            </span>
            <span className="relative ml-2 hidden text-sm font-semibold sm:inline">
              Previous
            </span>

            {previousSentencePreview && (
              <span
                className="pointer-events-none absolute left-full top-1/2 ml-3 hidden w-52 -translate-y-1/2 rounded-xl border border-slate-200/90 bg-white/95 px-3 py-2 text-left opacity-0 shadow-[0_12px_32px_-18px_rgba(15,23,42,0.45)] backdrop-blur-xl transition-all duration-180 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:opacity-100 sm:block"
                role="tooltip"
              >
                <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Previous sentence
                </span>
                <span className="mt-0.5 block truncate text-xs font-medium text-slate-700">
                  {previousSentencePreview}
                </span>
              </span>
            )}
          </button>
        )}

        {isLastSegment ? (
          <div
            className="absolute inset-y-0 right-0 w-12 sm:w-32"
            aria-hidden="true"
          />
        ) : (
          <button
            type="button"
            onClick={() =>
              navigateToSentence(currentSentence + 1)
            }
            aria-label="Next sentence"
            className="group absolute inset-y-0 right-0 z-30 flex w-12 items-center justify-end px-3 text-slate-500 outline-none transition-colors duration-200 hover:text-blue-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400/55 sm:w-32 sm:justify-center sm:px-4"
          >
            <span
              className="pointer-events-none absolute inset-y-0 left-0 right-0 bg-gradient-to-l from-blue-100/70 via-blue-50/35 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
              aria-hidden="true"
            />

            <span className="relative mr-2 hidden text-sm font-semibold sm:inline">
              Next
            </span>
            <span
              className="relative text-xl leading-none transition-transform duration-200 group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5"
              aria-hidden="true"
            >
              ›
            </span>

            {nextSentencePreview && (
              <span
                className="pointer-events-none absolute right-full top-1/2 mr-3 hidden w-52 -translate-y-1/2 rounded-xl border border-slate-200/90 bg-white/95 px-3 py-2 text-left opacity-0 shadow-[0_12px_32px_-18px_rgba(15,23,42,0.45)] backdrop-blur-xl transition-all duration-180 group-hover:opacity-100 group-focus-visible:opacity-100 sm:block"
                role="tooltip"
              >
                <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Next sentence
                </span>
                <span className="mt-0.5 block truncate text-xs font-medium text-slate-700">
                  {nextSentencePreview}
                </span>
              </span>
            )}
          </button>
        )}

        <div className="relative z-10 min-h-[102px] px-14 pb-3 pt-[43px] sm:px-36 sm:pb-4 sm:pt-[46px]">
          <div className="absolute left-1/2 top-1.5 z-40 -translate-x-1/2">
            {renderSentenceStatus()}
          </div>

        {enableWordActions &&
          showWords &&
          showSelectionMenu &&
          normalizedSelection &&
          practiceMode !== "shadowing" && (
            <div className="absolute left-1/2 top-[50px] -translate-x-1/2 z-50 pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-2 px-2 py-2 rounded-2xl bg-slate-900/95 text-white shadow-2xl border border-slate-700">
                <button
                  type="button"
                  onClick={playSelectedRange}
                  className="px-3 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm font-medium transition whitespace-nowrap"
                >
                  ▶ Play selected
                </button>

                <button
                  type="button"
                  onClick={clearSelection}
                  className="px-3 py-1.5 rounded-xl bg-white text-gray-700 hover:bg-slate-100 text-sm font-medium transition whitespace-nowrap border border-slate-300 shadow-sm"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {showWordLane ? (
            <div
              className={[
                "transition-[opacity,transform] duration-[180ms] ease-out will-change-transform",
                transitionPhase === "exit"
                  ? transitionDirection > 0
                    ? "-translate-x-2 opacity-0"
                    : "translate-x-2 opacity-0"
                  : transitionPhase === "enter-prep"
                  ? transitionDirection > 0
                    ? "translate-x-2 opacity-0"
                    : "-translate-x-2 opacity-0"
                  : "translate-x-0 opacity-100",
              ].join(" ")}
            >
        {showWords ? (
        <div className="flex flex-wrap gap-2 justify-center items-center">
          {segment?.words?.map((item, i) => {
            const matchedAssessment = mappedAssessmentWords[i];
            const pronunciation = matchedAssessment?.PronunciationAssessment;
            const phonemes = matchedAssessment?.Phonemes || [];
            const score = pronunciation?.AccuracyScore;
            const errorType = pronunciation?.ErrorType;

            const isActive = currentWord === i;

            const isShadowCueWord =
              practiceMode === "shadowing" &&
              isCueActive &&
              cuePulseCount >= 1 &&
              i === 0;

            const isInSelection =
              normalizedSelection &&
              i >= normalizedSelection.start &&
              i <= normalizedSelection.end;

            const isMultiSelection =
              normalizedSelection &&
              normalizedSelection.start !== normalizedSelection.end;

            const onWordClick = !enableWordActions
              ? undefined
              : practiceMode === "shadowing"
              ? () => handleWordPlay(i, item)
              : (e) => {
                  if (isMultiSelection) return;
                  openWordMenu(e, item.text, i);
                };

            const assessmentClass = getWordAssessmentClasses(score, errorType);

            const idleClass = [
              assessmentClass,
              "shadow-sm hover:-translate-y-px hover:border-blue-300 hover:bg-blue-50/60 hover:shadow-md",
            ].join(" ");
            const activeClass = getActiveRingClass(practiceMode);

            const selectionClass =
              "bg-gradient-to-r from-green-300 to-green-300 text-white font-semibold border border-green-500 shadow-lg shadow-green-300/30 scale-105";



            return (
              <span
                key={i}
                onClick={onWordClick}
                onMouseDown={
                  enableWordActions ? (e) => handleWordMouseDown(i, e) : undefined
                }
                onMouseEnter={
                  enableWordActions ? () => handleWordMouseEnter(i) : undefined
                }
                onMouseUp={enableWordActions ? handleWordMouseUp : undefined}
                className={[
                  "text-lg sm:text-xl font-medium px-3.5 py-2.5 rounded-xl transition-[transform,box-shadow,border-color,background-color] duration-180 cursor-pointer relative overflow-hidden select-none flex flex-col items-center gap-0.5 border min-w-[68px] will-change-transform",
                  isInSelection
                    ? selectionClass
                    : isActive
                    ? `${idleClass} ${activeClass}`
                    : idleClass,
                ].join(" ")}
                title={
                  enableWordActions && matchedAssessment
                    ? `${matchedAssessment?.Word || item.text}${
                        typeof score === "number" ? ` • Score: ${score}` : ""
                      }${errorType ? ` • ${errorType}` : ""}`
                    : practiceMode === "shadowing"
                    ? "Tap to play from here"
                    : "Drag to select words"
                }
              >
                <div className="flex items-center justify-center gap-1 h-2">
                  {phonemes.length > 0 ? (
                    phonemes.map((ph, idx) => (
                      <span
                        key={idx}
                        className={`w-2 h-2 rounded-full ${getPhonemeDotColor(
                          ph?.PronunciationAssessment?.AccuracyScore
                        )} ring-1 ring-white shadow-sm`}
                        title={`${ph?.Phoneme || "phoneme"}: ${
                          ph?.PronunciationAssessment?.AccuracyScore ?? "N/A"
                        }`}
                      />
                    ))
                  ) : matchedAssessment ? (
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${getWordScoreColor(
                        score,
                        errorType
                      )} ring-1 ring-white shadow-sm`}
                    />
                  ) : null}
                </div>

                <span className="text-gray-800 text-center leading-tight break-words">
                  {item.text}
                </span>

                {isShadowCueWord && (
                  <div className="absolute inset-0 bg-white/20 animate-ping rounded-xl" />
                )}
              </span>
            );
          })}
        </div>
        ) : (
          <div
            className="flex min-h-[72px] flex-wrap items-center justify-center gap-2"
            aria-label="Sentence words are hidden"
          >
            {segment?.words?.map((item, i) => {
              const isActive = isPlayingSegment && currentWord === i;
              const estimatedWidth = Math.max(68, Math.min(180,
                String(item?.text || "").length * 11 + 28
              ));

              return (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{ width: `${estimatedWidth}px` }}
                  className={[
                    "h-[50px] rounded-xl border transition-all duration-300",
                    "bg-white/80 border-slate-200/90 ring-1 ring-white",
                    isActive
                      ? getActiveRingClass("echoing")
                      : "shadow-sm",
                  ].join(" ")}
                />
              );
            })}
          </div>
        )}
            </div>
          ) : (
            <div className="min-h-[28px]" aria-hidden="true" />
          )}
        </div>
      </div>
    </div>
  );
};

export default SentencePracticeBlock;